import type { ReactNode } from 'react'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/Button'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { SplitView, SplitViewMain, SplitViewSide } from '@/components/SplitView'
import { TabBar, TabItem } from '@/components/TabBar'
import styles from './ClientUserDetails.module.css'
import { isSupported, copy } from 'clipbrd'
import { type ClientUserDetailsTab } from '@/features/client-user-management/ClientUserDetails/ClientUserDetailsViewModel'

export interface ClientUserDetailsProps {
  header: ReactNode
  tabs: ReactNode
  information: ReactNode
  /**
   * The content of the selected tab.
   */
  tabContent: ReactNode
}

export function ClientUserDetails({
  header,
  tabs,
  information,
  tabContent,
}: ClientUserDetailsProps) {
  return (
    <div className={styles.root}>
      <div className={styles.main}>
        {header}
        {tabs}
        <SplitView flexChild>
          <SplitViewSide narrow>
            <div className={styles.userInfoRoot}>
              <div className={styles.userInfoScrollContainer}>
                {information}
              </div>
            </div>
          </SplitViewSide>
          <SplitViewMain>
            <div className={styles.userTabRoot}>{tabContent}</div>
          </SplitViewMain>
        </SplitView>
      </div>
    </div>
  )
}

export interface ClientUserDetailsHeaderProps {
  avatar: string | null
  displayName: string
  email: string
  canCreateJob: boolean
  onCreateJob: () => void
}

export function ClientUserDetailsHeader({
  avatar,
  displayName,
  email,
  canCreateJob,
  onCreateJob,
}: ClientUserDetailsHeaderProps) {
  const copyEmailToClipboard = (email: string) => {
    if (isSupported()) {
      copy(email)
    }
  }

  return (
    <div className={styles.header}>
      <div className={styles.headerInfo}>
        <Avatar size={48} subject={{ avatar }} />
        <div className={styles.headerInfoText}>
          <h1 className={styles.headerInfoTitle}>{displayName}</h1>
          <div className={styles.headerInfoSubtitle}>
            <p className={styles.headerInfoEmail}>{email}</p>
            <IconButton
              name="copy"
              label="Copy email"
              className={styles.headerInfoCopyIcon}
              size={16}
              inline
              onPress={() => copyEmailToClipboard(email)}
            />
          </div>
        </div>
      </div>
      <div className={styles.headerActions}>
        <Dropdown
          autoDismiss
          position="bottom-end"
          button={
            <Button
              label="Actions"
              variant="secondary"
              className={styles.actionsButton}
              iconRight="dropdown"
              iconRightFlush
              testId="user-details-actions"
            />
          }
        >
          <DropdownTitle>Actions</DropdownTitle>
          {canCreateJob && (
            <DropdownItem
              onPress={onCreateJob}
              buttonTestId="user-details-actions-create-job"
            >
              Create job for user
            </DropdownItem>
          )}
        </Dropdown>
      </div>
    </div>
  )
}

export interface ClientUserDetailsTabsProps {
  tabs: ClientUserDetailsTab[]
  currentTab: ClientUserDetailsTab
  onTabChange: (tab: ClientUserDetailsTab) => void
}

export function ClientUserDetailsTabs({
  tabs,
  currentTab,
  onTabChange,
}: ClientUserDetailsTabsProps) {
  return (
    <div className={styles.tabs}>
      <TabBar>
        {tabs.map((tab) => (
          <TabItem
            key={tab}
            active={currentTab === tab}
            onPress={() => onTabChange(tab)}
            testId={`user-details-tab-${tab.toLowerCase()}`}
          >
            {tab}
          </TabItem>
        ))}
      </TabBar>
    </div>
  )
}
